import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow, PrimaryButton } from "@/components/fionova/Primitives";
import { getPaymentStatus } from "@/services/unsaidService";
import { onboardingState } from "@/lib/onboardingState";
import { useAuth } from "@/context/AuthContext";
import { trackPersistentOnce, FOUNDERS } from "@/lib/analytics";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("pending"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.entitled || s.payment_status === "paid") {
          onboardingState.setSubscribed(true);
          // GA4 purchase — fired ONLY on authoritative server entitlement, and
          // deduped by the Stripe session id so success-page refreshes / revisits
          // / webhook retries can never double-count the conversion.
          if (s.entitled) {
            if (s.subscription_status === "trialing") {
              // Free-trial activation (no charge today) — not a paid purchase yet.
              trackPersistentOnce(`trial_${sessionId}`, "trial_started", {
                transaction_id: sessionId, plan: FOUNDERS.plan,
              });
            } else {
              trackPersistentOnce(`purchase_${sessionId}`, "purchase", {
                transaction_id: sessionId,
                currency: FOUNDERS.currency,
                value: FOUNDERS.value,
                plan: FOUNDERS.plan,
              });
            }
          }
          await refresh();
          setState("paid");
          setTimeout(() => navigate("/app?chat=1"), 1600);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries >= 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [navigate, refresh]);

  return (
    <OnboardingLayout testId="page-payment-success" contentClassName="items-center justify-center py-12 text-center">
      {state === "checking" && (
        <>
          <div className="flex items-center gap-1.5" data-testid="success-checking">
            {[0, 1, 2].map((i) => (<span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />))}
          </div>
          <p className="mt-6 text-base text-muted-foreground">Confirming your subscription…</p>
        </>
      )}
      {state === "paid" && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center" data-testid="success-paid">
          <Logo size={72} />
          <span className="mt-8 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Check className="h-7 w-7" strokeWidth={2.5} />
          </span>
          <Eyebrow className="mt-6">You're in</Eyebrow>
          <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-foreground">Welcome to FioNova.</h1>
          <p className="mt-4 max-w-sm text-base leading-relaxed text-muted-foreground">
            Ellie will keep building on everything you've shared. This is where it begins — for real this time.
          </p>
          <div className="mt-8 w-full max-w-[280px]">
            <PrimaryButton data-testid="success-enter" onClick={() => navigate("/app?chat=1")}>Enter FioNova</PrimaryButton>
          </div>
        </motion.div>
      )}
      {state === "pending" && (
        <div className="flex flex-col items-center" data-testid="success-pending">
          <Eyebrow>Almost there</Eyebrow>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">We're finalizing your subscription.</h1>
          <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
            If you completed payment, it may take a moment to confirm. You can safely continue.
          </p>
          <div className="mt-8 w-full max-w-[280px]">
            <PrimaryButton data-testid="success-retry" onClick={() => window.location.reload()}>Check again</PrimaryButton>
          </div>
        </div>
      )}
    </OnboardingLayout>
  );
}
